class EnvironmentFiguresRoller {
  constructor(selector = '.js-figures-roller') {
    this.targets = Array.from(document.querySelectorAll(selector));

    if (!this.targets.length) {
      return;
    }

    this.duration = 2200;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.targets.forEach((target) => this.setup(target));
  }

  setup(target) {
    if (target.dataset.figuresRollerReady === '1') {
      return;
    }

    const raw = target.dataset.countTo != null
      ? target.dataset.countTo
      : target.textContent.trim();
    const countTo = Number(raw);

    if (Number.isNaN(countTo) || countTo < 0) {
      return;
    }

    const digitLength = Math.max(
      Number(target.dataset.digitLength) || 0,
      String(Math.floor(countTo)).length,
      1
    );

    target.dataset.figuresRollerReady = '1';
    target.setAttribute('aria-label', String(countTo).padStart(digitLength, '0'));
    target.classList.add('is-roller');

    const places = this.buildPlaces(target, countTo, digitLength);
    target._figuresRoller = {
      countTo,
      digitLength,
      places,
      started: false,
    };

    this.setValue(target, 0);

    if (this.reducedMotion) {
      this.setValue(target, countTo);
      return;
    }

    const card = target.closest('.p-environment__figures-card') || target;

    if (!('IntersectionObserver' in window)) {
      this.start(target);
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      const visible = entries.some((entry) => entry.isIntersecting);
      if (!visible) {
        return;
      }

      this.start(target);
      observer.disconnect();
    }, {
      root: null,
      threshold: 0.35,
    });

    observer.observe(card);
  }

  buildPlaces(target, countTo, digitLength) {
    target.textContent = '';

    const places = [];

    for (let placeIndex = digitLength - 1; placeIndex >= 0; placeIndex -= 1) {
      const divisor = 10 ** placeIndex;
      const maxIndex = Math.floor(countTo / divisor);

      const digit = document.createElement('span');
      digit.className = 'p-environment__figures-digit';
      digit.setAttribute('aria-hidden', 'true');

      const windowEl = document.createElement('span');
      windowEl.className = 'p-environment__figures-digit-window';

      const strip = document.createElement('span');
      strip.className = 'p-environment__figures-digit-strip';

      for (let i = 0; i <= maxIndex; i += 1) {
        const char = document.createElement('span');
        char.className = 'p-environment__figures-digit-char';
        char.textContent = String(i % 10);
        strip.appendChild(char);
      }

      if (!strip.children.length) {
        const char = document.createElement('span');
        char.className = 'p-environment__figures-digit-char';
        char.textContent = '0';
        strip.appendChild(char);
      }

      windowEl.appendChild(strip);
      digit.appendChild(windowEl);
      target.appendChild(digit);

      places.push({ divisor, strip, maxIndex });
    }

    return places;
  }

  setValue(target, value) {
    const state = target._figuresRoller;
    if (!state) {
      return;
    }

    state.places.forEach(({ divisor, strip, maxIndex }) => {
      // 連続位置で流しつつ、ストリップ末尾（整数桁）を超えない。
      // 例: 15 の十の位は 15/10=1.5 になり、1 と空白の間で止まってしまう。
      const index = Math.min(value / divisor, maxIndex);
      strip.style.transform = `translate3d(0, ${(-index).toFixed(4)}em, 0)`;
    });
  }

  easeInOutCubic(t) {
    return t < 0.5
      ? 4 * t * t * t
      : 1 - ((-2 * t + 2) ** 3) / 2;
  }

  start(target) {
    const state = target._figuresRoller;
    if (!state || state.started) {
      return;
    }

    state.started = true;

    if (state.countTo === 0) {
      this.setValue(target, 0);
      return;
    }

    const startTime = performance.now();

    const update = (now) => {
      const progress = Math.min((now - startTime) / this.duration, 1);
      const eased = this.easeInOutCubic(progress);
      const value = progress === 1
        ? state.countTo
        : state.countTo * eased;

      this.setValue(target, value);

      if (progress < 1) {
        requestAnimationFrame(update);
      }
    };

    requestAnimationFrame(update);
  }
}
